import { useEffect, useRef, useState } from 'react';
import { loaderPhaseAt, type LoaderPhase } from './mascot.types';

/** How long the celebration is allowed to hold a full-screen transition. */
const DONE_HOLD_MS = 220;

export interface LoaderPhaseState {
  /** The phase to render, or null when nothing should be shown at all. */
  phase: LoaderPhase | null;
  /** True once the run finished and the celebration is playing out. */
  celebrating: boolean;
}

/**
 * Turns the network store's run into the staged loader sequence.
 *
 * The store decides what a run is: it opens on the first tracked request and stays open through
 * the gaps between sequential requests, closing only once nothing has been pending for its
 * grace window. This hook trusts that.
 *
 *  - `runId` identifies the run. While it is unchanged the phase clock keeps advancing —
 *    "Starting… / Gathering… / Working…" progresses once for the whole piece of work, never
 *    restarting in a seam.
 *  - `startedAt` anchors the phase clock to when the run's first request actually began.
 *  - The clock only advances while `pending` — during a seam it holds the phase it had, so a
 *    burst that happens to finish just before the 450ms mark never flashes a spinner it did not
 *    earn. When the run ends: if the loader was actually on screen it plays "Done" for a short
 *    hold, otherwise it disappears silently.
 *
 * Nothing renders for the first 450ms of a run: most work finishes inside that window and a
 * spinner that flashes for 200ms reads as jank, not progress.
 */
export function useLoaderPhase(
  runId: number,
  startedAt: number | null,
  pending: boolean,
  runOpen: boolean,
): LoaderPhaseState {
  const [phase, setPhase] = useState<LoaderPhase | null>(null);
  const [celebrating, setCelebrating] = useState(false);
  const origin = useRef(0);
  const currentRun = useRef(-1);
  const wasShown = useRef(false);
  // Elapsed the phase clock had reached last time work was actually pending — the clock is
  // frozen here during a seam so a gap does not push it across a threshold.
  const heldElapsed = useRef(0);

  useEffect(() => {
    if (runOpen && currentRun.current !== runId) {
      currentRun.current = runId;
      origin.current = startedAt ?? performance.now();
      wasShown.current = false;
      heldElapsed.current = 0;
    }

    if (runOpen && pending) {
      setCelebrating(false);
      const advance = () => {
        heldElapsed.current = performance.now() - origin.current;
        const next = loaderPhaseAt(heldElapsed.current);
        if (next) wasShown.current = true;
        setPhase(next);
      };
      advance();
      const timer = setInterval(advance, 100);
      return () => clearInterval(timer);
    }

    if (runOpen) {
      // Seam: nothing pending right now, but the run is still open. Hold the phase steady.
      setPhase(loaderPhaseAt(heldElapsed.current));
      return;
    }

    // Run over.
    currentRun.current = -1;
    if (!wasShown.current) {
      setPhase(null);
      return;
    }
    wasShown.current = false;
    setPhase('done');
    setCelebrating(true);
    const timer = setTimeout(() => {
      setPhase(null);
      setCelebrating(false);
    }, DONE_HOLD_MS);
    return () => clearTimeout(timer);
  }, [runId, startedAt, pending, runOpen]);

  return { phase, celebrating };
}
